import React, { useEffect, useState } from "react"
import { Input } from "reactstrap"
import ExprBuilder from "pages/LocationConfiguration/ExprBuilder"
import PayloadAutocomplete from "components/Common/PayloadAutocomplete"
import { useSamplePayloadPaths } from "utils/samplePayloadPaths"
import "pages/LocationConfiguration/ExprBuilder.scss"

const pathToFmtToken = path => {
  const segments = path.split(".")
  if (segments.length <= 1) return `{${path}}`
  return `{${segments[0]}[${segments.slice(1).join("][")}]}`
}

const fmtTokenToPath = token => {
  const match = token.match(/^\{([^{}]+)\}$/)
  if (!match) return null
  const inner = match[1]
  if (!inner.includes("[")) return inner
  return inner.replace(/\]\[/g, ".").replace(/\[/g, ".").replace(/\]/g, "")
}

const parseFmtValue = value => {
  if (!value) return []
  const tokens = value.match(/\{[^{}]+\}/g) || []
  return tokens.map(token => fmtTokenToPath(token) ?? token)
}

const detectSeparator = value => {
  const match = (value || "").match(/\}([^{}]+)\{/)
  return match ? match[1] : ","
}

const buildFmtValue = (parts, separator) =>
  parts.map(pathToFmtToken).join(separator)

const PathBody = ({ value, onChange, pathOptions }) => (
  <PayloadAutocomplete
    options={pathOptions}
    placeholder="e.g. destLoc.city or stopRefs.PO"
    value={value || ""}
    onChange={onChange}
  />
)

const LiteralBody = ({ value, onChange }) => (
  <Input
    bsSize="sm"
    placeholder="Enter literal value"
    value={value || ""}
    onChange={e => onChange(e.target.value)}
  />
)

const FormatBody = ({ value, onChange, pathOptions }) => {
  const [separator, setSeparator] = useState(() => detectSeparator(value))
  const [draft, setDraft] = useState("")
  const [parts, setParts] = useState(() => parseFmtValue(value))

  // Rehydrate from external value when it changes (e.g., editing an existing variable).
  useEffect(() => {
    setParts(parseFmtValue(value))
  }, [value])

  const commit = next => {
    setParts(next)
    onChange(buildFmtValue(next, separator))
  }

  const addPart = part => {
    if (!part) return
    commit([...parts, part])
    setDraft("")
  }

  const removePart = index => {
    commit(parts.filter((_, i) => i !== index))
  }

  const updateSeparator = sep => {
    setSeparator(sep)
    onChange(buildFmtValue(parts, sep))
  }

  return (
    <div className="value-fmt">
      <div className="value-fmt-chips" aria-label="Format parts">
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <span className="value-fmt-separator-indicator" aria-hidden="true">
                {separator}
              </span>
            )}
            <span className="value-fmt-chip">
              {pathToFmtToken(part)}
              <button
                type="button"
                className="value-fmt-chip-remove"
                onClick={() => removePart(index)}
                aria-label={`Remove ${part}`}
              >
                <i className="mdi mdi-close-circle" />
              </button>
            </span>
          </React.Fragment>
        ))}
        {parts.length === 0 && (
          <span className="value-fmt-chips-empty">No parts yet — add a path below</span>
        )}
      </div>
      <div className="value-fmt-row">
        <div className="value-fmt-input">
          <PayloadAutocomplete
            options={pathOptions}
            placeholder="Add path (Enter to commit)"
            value={draft}
            onChange={setDraft}
            onCommit={addPart}
          />
        </div>
        <div className="value-fmt-separator">
          <label className="qed-builder-field-label">Separator</label>
          <Input
            bsSize="sm"
            value={separator}
            onChange={e => updateSeparator(e.target.value)}
          />
        </div>
      </div>
    </div>
  )
}

const ValueBuilder = ({ type, value, onChange }) => {
  const payloadPaths = useSamplePayloadPaths()
  const pathOptions = payloadPaths.map(item => ({ value: item.path, label: item.label }))
  // PATH does literal key traversal — it has no `stopId` variable and can't
  // index lists, so paths like stopIdsV2[stopId].orderId resolve to None. Only
  // suggest bracket-free paths for PATH; the rest belong in EXPR.
  const literalPathOptions = pathOptions.filter(o => !o.value.includes("["))

  if (type === "EXPR") return <ExprBuilder onChange={onChange} initialValue={value} />
  if (type === "FMT") return <FormatBody value={value} onChange={onChange} pathOptions={pathOptions} />
  if (type === "LIT") return <LiteralBody value={value} onChange={onChange} />
  return <PathBody value={value} onChange={onChange} pathOptions={literalPathOptions} />
}

export default ValueBuilder
